import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { getAlertsFromData, mockCurrentData, mockPreviousData } from '../data/alertSystemLogic';

export const AlertSystem = () => {
  const [activeAlerts, setActiveAlerts] = useState(getAlertsFromData(mockCurrentData, mockPreviousData));
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showActionPlan, setShowActionPlan] = useState(false);

  const acknowledgeAlert = (id) => {
    setActiveAlerts(prev => prev.filter(a => a.id !== id));
    setSelectedAlert(null);
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 uppercase tracking-tight">Environment Alert System</h1>
          <p className="text-slate-500 mt-1">Automatisk risikodeteksjon og varslingshåndtering.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={<SafeIcon name="Settings" />}>Konfigurer Triggere</Button>
          <Button variant="primary" icon={<SafeIcon name="Bell" />}>Varslingslogg</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Active Alerts List */}
        <div className="lg:col-span-2 space-y-4">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider ml-1">Aktive Varsler ({activeAlerts.length})</h3>
          <AnimatePresence mode="popLayout">
            {activeAlerts.length > 0 ? activeAlerts.map((alert) => (
              <motion.div
                key={alert.id}
                layout
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={() => setSelectedAlert(alert)}
                className={`group cursor-pointer p-6 rounded-3xl border-2 transition-all bg-white hover:shadow-xl ${
                  selectedAlert?.id === alert.id ? 'border-indigo-600 shadow-lg shadow-indigo-100' : 'border-slate-100 hover:border-slate-200'
                }`}
              >
                <div className="flex gap-6 items-start">
                  <div className={`p-4 rounded-2xl ${alert.severity === 'Critical' ? 'bg-rose-50 text-rose-600' : 'bg-orange-50 text-orange-600'} shrink-0`}>
                    <SafeIcon name={alert.icon} className="text-2xl" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex justify-between items-center">
                      <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${alert.severity === 'Critical' ? 'text-rose-600' : 'text-orange-600'}`}>
                        {alert.severity} • {alert.type}
                      </span>
                      <span className="text-xs text-slate-400 font-bold">Detektert: I dag, 09:12</span>
                    </div>
                    <h4 className="text-lg font-black text-slate-900 uppercase tracking-tighter">{alert.trigger}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{alert.message}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); acknowledgeAlert(alert.id); }}>
                      Kvittér
                    </Button>
                  </div>
                </div>
              </motion.div>
            )) : (
              <div className="py-20 text-center bg-emerald-50 rounded-[40px] border-2 border-dashed border-emerald-200">
                <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <SafeIcon name="Check" className="text-4xl text-emerald-500" />
                </div>
                <h3 className="text-xl font-bold text-emerald-900">Alt i orden!</h3>
                <p className="text-emerald-700/70 max-w-xs mx-auto text-sm">Ingen aktive risiko-varsler detektert i systemet akkurat nå.</p>
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Action Panel */}
        <div className="space-y-6">
          <AnimatePresence mode="wait">
            {selectedAlert ? (
              <motion.div
                key="action-panel"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="space-y-6"
              >
                <Card className="border-indigo-600 border-2">
                  <CardHeader 
                    title="Anbefalte Tiltak" 
                    subtitle="AI-generert responsplan"
                    icon={<SafeIcon name="Zap" className="text-indigo-600" />}
                  />
                  <CardContent className="space-y-4">
                    {selectedAlert.recommendations.map((rec, i) => (
                      <div key={i} className="flex gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 items-start">
                        <div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                          {i + 1}
                        </div>
                        <p className="text-sm font-bold text-slate-700">{rec}</p>
                      </div>
                    ))}
                    <div className="pt-4 space-y-3">
                      <Button variant="primary" className="w-full py-4" onClick={() => setShowActionPlan(true)}>
                        Opprett Handlingsplan
                      </Button>
                      <Button variant="secondary" className="w-full">
                        Planlegg Oppfølgingsmåling
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900 border-none text-white">
                  <CardContent className="p-8 space-y-4">
                    <SafeIcon name="Mail" className="text-3xl text-indigo-400" />
                    <h4 className="font-bold">Automatisert Varsling</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      E-post er sendt til ansvarlig HR-partner og avdelingsleder for å sikre umiddelbar oppfølging.
                    </p>
                    <div className="pt-2 flex flex-col gap-2">
                      <div className="flex justify-between text-[10px] font-bold text-slate-500">
                        <span>HR VARSLET</span>
                        <span className="text-emerald-500 font-black">SENDT</span>
                      </div>
                      <div className="flex justify-between text-[10px] font-bold text-slate-500">
                        <span>LEDER VARSLET</span>
                        <span className="text-emerald-500 font-black">SENDT</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <Card className="bg-slate-50 border-dashed border-2 border-slate-200">
                <CardContent className="p-12 text-center space-y-4">
                  <SafeIcon name="Info" className="text-4xl text-slate-300 mx-auto" />
                  <p className="text-sm text-slate-400 font-medium leading-relaxed">
                    Velg et varsel fra listen for å se dypere analyse og anbefalte tiltak.
                  </p>
                </CardContent>
              </Card>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Action Plan Modal (Simulated) */}
      <AnimatePresence>
        {showActionPlan && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/80 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }} 
              animate={{ scale: 1, y: 0 }}
              className="max-w-xl w-full bg-white rounded-[40px] shadow-2xl overflow-hidden p-10 space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon name="Edit3" className="text-2xl" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">Opprett Handlingsplan</h2>
                <p className="text-slate-500 text-sm">Definer tiltak og ansvarlige for å håndtere risikoen.</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tiltak</label>
                  <textarea 
                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none" 
                    rows="3"
                    defaultValue={selectedAlert?.recommendations.join(', ')}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Ansvarlig</label>
                    <input type="text" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" placeholder="Navn..." />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Frist</label>
                    <input type="date" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="secondary" className="flex-1" onClick={() => setShowActionPlan(false)}>Avbryt</Button>
                <Button variant="primary" className="flex-1" onClick={() => { setShowActionPlan(false); acknowledgeAlert(selectedAlert.id); }}>Lagre & Aktiver</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};